import { useStyletron } from '@thanx/uikit/theme'
import { locationsApi } from 'api/locations'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect, useMemo, useState } from 'react'
import FilterSelect from '../FilterSelect'
import HelpLinks from '../HelpLinks'
import LocationDetailsModal from '../LocationDetailsModal'
import type { ConsolidatedLocation } from '../mockLocations'
import SearchBar from '../SearchBar'
import SelectionRow from '../SelectionRow'
import { compareLocations } from './helpers'
import LocationRow from './LocationRow'
import { PageSizeSelector, Pagination } from './Pagination'
import SortableHeader from './SortableHeader'
import {
  DEFAULT_PAGE_SIZE,
  type PageSize,
  type SortKey,
  type SortState,
} from './types'

const t = buildTranslate('locations.location_list')

type LocationsTabProps = {
  locations: ConsolidatedLocation[]
  isLoading: boolean
  isError: boolean
  selected: Set<number>
  setSelected: React.Dispatch<React.SetStateAction<Set<number>>>
  onToggleOrdering: (location: ConsolidatedLocation) => void
  rowMutationLoading: boolean
  // True when the current merchant is a Toast merchant — surfaces the
  // ordering-status column, per-row overflow menu, multi-select
  // checkboxes, and the SelectionRow. Non-Toast merchants get a
  // read-only directory per the Figma "Non-Toast merchant will not
  // have:" note (no batch actions / no enable-disable / no ordering
  // status column / no overflow column). Defaults to true so existing
  // tests don't need updates.
  showOrderingControls?: boolean
  statusFilter: 'errors' | 'notices' | null
}

const LocationsTab = ({
  locations,
  isLoading,
  isError,
  selected,
  setSelected,
  onToggleOrdering,
  rowMutationLoading,
  showOrderingControls = true,
  statusFilter,
}: LocationsTabProps) => {
  const [detailsModalLocationId, setDetailsModalLocationId] = useState<
    number | null
  >(null)
  // The Details modal needs phone / time_zone / address / name — all of
  // which are exposed at every LocationEntity visibility tier. Lazy-fetch
  // the locations list only when the operator has actually clicked
  // "View Details" so the page-load cost is zero for users who never open
  // the modal (the majority of visits per Darshan's 2026-05-21 review).
  // RTK Query caches the first response, so subsequent clicks across
  // rows are instant.
  const { data: locationsData } = locationsApi.useGetLocationsQuery(
    {},
    { skip: detailsModalLocationId == null }
  )
  const detailsModalLocation = useMemo(
    () =>
      detailsModalLocationId == null
        ? null
        : locationsData?.locations.find(l => l.id === detailsModalLocationId) ??
          null,
    [detailsModalLocationId, locationsData]
  )
  const [css] = useStyletron()
  const [sort, setSort] = useState<SortState>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE)
  // Country filter intentionally absent for now — the OrderingConfig
  // payload doesn't carry country, and shipping a no-op dropdown that
  // looks selectable is worse than not shipping one. Add back once the
  // BE exposes `l.country` (Thanx is US-only today, so single-option).
  const [state, setState] = useState('')
  const [query, setQuery] = useState('')

  // Filter options are derived from the loaded data so the dropdowns only
  // ever show values that are actually selectable. Country isn't on the
  // OrderingConfig response today (the BE only exposes the address fields);
  // for the US-only Thanx footprint we synthesize 'United States' when
  // any state is present and skip the option entirely otherwise.
  const stateOptions = useMemo(
    () =>
      Array.from(new Set(locations.map(l => l.state).filter(Boolean))).sort(),
    [locations]
  )

  // Reset to page 1 whenever a filter changes so the user doesn't land on
  // an out-of-range page after the filtered list shrinks. Also clear the
  // selection so bulk actions never operate on rows the user can't see;
  // otherwise "Select all → narrow filter → Disable selected" silently
  // mutates rows off-screen.
  const handleStateChange = (next: string) => {
    setState(next)
    setCurrentPage(1)
    setSelected(new Set())
  }
  const handleQueryChange = (next: string) => {
    setQuery(next)
    setCurrentPage(1)
    setSelected(new Set())
  }

  // statusFilter is parent-owned (driven by nav/URL), so unlike state/query
  // above there's no local onChange handler to hook the reset into; a
  // useEffect keyed on the prop is the only place to catch the change.
  useEffect(() => {
    setCurrentPage(1)
    setSelected(new Set())
  }, [statusFilter, setSelected])

  const filteredLocations = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const matchesQuery = (loc: ConsolidatedLocation) => {
      if (needle === '') return true
      return [loc.name, loc.street, loc.city, loc.state, loc.zip].some(field =>
        field.toLowerCase().includes(needle)
      )
    }
    return locations.filter(
      l =>
        (state === '' || l.state === state) &&
        matchesQuery(l) &&
        (statusFilter === null || l.locationStatus.kind === statusFilter)
    )
  }, [locations, state, query, statusFilter])

  const handleSort = (key: SortKey) => {
    setSort(prev =>
      prev?.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    )
  }

  // Reset to page 1 when the user picks a different page size — otherwise
  // a current page of 4 (under 10/page) would silently land on row 76 when
  // they switch to 25/page, which is disorienting. Anchoring back to 1
  // matches what most admin tables do.
  const handlePageSizeChange = (size: PageSize) => {
    setPageSize(size)
    setCurrentPage(1)
  }

  const sortedLocations = useMemo(() => {
    if (!sort) return filteredLocations
    const dir = sort.direction === 'asc' ? 1 : -1
    return [...filteredLocations].sort(
      (a, b) => compareLocations(a, b, sort.key) * dir
    )
  }, [sort, filteredLocations])

  const totalPages = Math.max(1, Math.ceil(sortedLocations.length / pageSize))
  // Clamp so a stale `currentPage` (e.g. after filtering shrinks the list)
  // never falls outside the valid range; the slice below would otherwise
  // render an empty page with no way back.
  const clampedPage = Math.min(currentPage, totalPages)
  const visibleLocations = sortedLocations.slice(
    (clampedPage - 1) * pageSize,
    clampedPage * pageSize
  )

  return (
    <div className={`p-l ${css({ backgroundColor: palette.grey05 })}`}>
      <SearchBar value={query} onChange={handleQueryChange} />
      <div className="d-flex mb-m">
        <FilterSelect
          label={t('filters.state')}
          value={state}
          options={stateOptions}
          onChange={handleStateChange}
          disabled={stateOptions.length === 0}
        />
      </div>
      {isLoading && (
        <p
          className={`body-text-4 mb-m ${css({ color: palette.grey70 })}`}
          role="status"
        >
          {t('loading')}
        </p>
      )}
      {isError && (
        <p
          className={`body-text-4 mb-m ${css({ color: palette.cayenne50 })}`}
          role="alert"
        >
          {t('load_error')}
        </p>
      )}
      {showOrderingControls && (
        <SelectionRow
          selectedCount={selected.size}
          // Scope "Select all" to the filtered/visible list so the
          // operator never bulk-acts on rows they can't see.
          onSelectAll={() =>
            setSelected(new Set(filteredLocations.map(l => l.id)))
          }
          onDeselectAll={() => setSelected(new Set())}
        />
      )}
      <table className="full-width" role="table">
        <thead>
          <tr>
            {showOrderingControls && (
              <th aria-label={t('table.checkbox_header')} />
            )}
            <SortableHeader
              label={t('table.location')}
              sortKey="name"
              sort={sort}
              onSort={handleSort}
            />
            <SortableHeader
              label={t('table.location_status')}
              sortKey="locationStatus"
              sort={sort}
              onSort={handleSort}
            />
            {showOrderingControls && (
              <SortableHeader
                label={t('table.ordering_status')}
                sortKey="orderingEnabled"
                sort={sort}
                onSort={handleSort}
              />
            )}
            <th aria-label={t('table.view_header')} />
            {showOrderingControls && (
              <th aria-label={t('table.row_overflow_header')} />
            )}
          </tr>
        </thead>
        <tbody>
          {visibleLocations.map(location => (
            <LocationRow
              key={location.id}
              location={location}
              selected={selected.has(location.id)}
              onToggle={id =>
                setSelected(prev => {
                  const next = new Set(prev)
                  if (next.has(id)) next.delete(id)
                  else next.add(id)
                  return next
                })
              }
              onToggleOrdering={onToggleOrdering}
              onViewDetails={loc => setDetailsModalLocationId(loc.id)}
              rowMutationLoading={rowMutationLoading}
              showOrderingControls={showOrderingControls}
            />
          ))}
        </tbody>
      </table>
      <div className="position-relative">
        <Pagination
          currentPage={clampedPage}
          totalPages={totalPages}
          onChange={setCurrentPage}
        />
        <div
          className={css({
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
          })}
        >
          <PageSizeSelector value={pageSize} onChange={handlePageSizeChange} />
        </div>
      </div>
      <HelpLinks />
      {detailsModalLocation && (
        <LocationDetailsModal
          isOpen={true}
          location={detailsModalLocation}
          onClose={() => setDetailsModalLocationId(null)}
        />
      )}
    </div>
  )
}

export default LocationsTab
